/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  Configuration,
  ConfigurationId,
} from '@daas-consulting/production/shared/cross/configuration';
import {
  AuthenticationViewContract,
  SecurityAuthenticationViewContractId,
} from '@daas-consulting/security/authentication/frontend/view_contracts';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { Observable, OperatorFunction, of, switchMap, throwError } from 'rxjs';
import { v4 as uuid } from 'uuid';

interface ResolveRequestOptions {
  req: HttpRequest<unknown>;
  token: string;
}

interface ResolveAuthenticatedRequestOptions {
  req: HttpRequest<unknown>;
  next: HttpHandler;
}

interface ResolveContinueWithRequestOrErrorOptions {
  req: HttpRequest<unknown>;
  next: HttpHandler;
}

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  // readonly #authenticationService: AuthenticationService;
  readonly #store: Store;
  readonly #authenticationViewModel: AuthenticationViewContract;

  // TODO: Inyectar por constructor.
  readonly #unauthorizedUrls: string[] = ['security/code'];
  readonly #configuration: Configuration;

  constructor(
    store: Store,
    @Inject(ConfigurationId) configuration: Configuration,
    @Inject(SecurityAuthenticationViewContractId)
    authenticationViewModel: AuthenticationViewContract
  ) {
    // this.#authenticationService = authenticationService;
    this.#store = store;
    this.#configuration = configuration;
    this.#authenticationViewModel = authenticationViewModel;
  }

  /**
   * Intercepta y manipula peticiones HTTP.
   * @param req Petición HTTP.
   * @param next Próximo manejador de la petición HTTP.
   * @returns Observable con la llamada al próximo manejador de la petición HTTP manipulada.
   */
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const requestRequiresAuthorization =
      this._requestRequiresAuthorization(req);
    let rv;
    if (requestRequiresAuthorization) {
      rv = this._resolveAuthenticatedRequest({ req, next });
    } else {
      rv = next.handle(req);
    }

    return rv;
  }

  private _requestRequiresAuthorization(req: HttpRequest<unknown>): boolean {
    const isFileRequest = this._isFileRequest(req);
    const hasAuthorizationHeader = this._hasRequestAuthorizationHeader(req);
    const isAnUnauthorizedUrl = this._isAnUnauthorizedUrl(req);
    const rv =
      !isFileRequest && !hasAuthorizationHeader && !isAnUnauthorizedUrl;
    return rv;
  }

  private _isFileRequest(req: HttpRequest<unknown>): boolean {
    const rv = /(\.html?|\.js|\.svg|\.json)/i.test(req.url);
    return rv;
  }

  private _hasRequestAuthorizationHeader(req: HttpRequest<unknown>): boolean {
    const rv =
      typeof req.headers === 'object' && req.headers.has('Authorization');
    return rv;
  }

  private _isAnUnauthorizedUrl(req: HttpRequest<unknown>): boolean {
    const rv =
      typeof req.url === 'string' &&
      this.#unauthorizedUrls.some((item: string) => req.url.indexOf(item) > -1);
    return rv;
  }

  private _resolveAuthenticatedRequest({
    req,
    next,
  }: ResolveAuthenticatedRequestOptions): Observable<HttpEvent<any>> {
    const rv = this.#authenticationViewModel.isAuthenticated$.pipe(
      this._resolveToken(),
      this._resolveContinueWithRequestOrError({ req, next })
    );
    return rv;
  }

  private _resolveContinueWithRequestOrError({
    req,
    next,
  }: ResolveContinueWithRequestOrErrorOptions): OperatorFunction<
    string,
    HttpEvent<any>
  > {
    return switchMap((token: string) => {
      let rv;
      const tokenToRequest = token || null;
      if (tokenToRequest) {
        const newReq = this._resolveRequest({ req, token: tokenToRequest });
        rv = next.handle(newReq);
      } else {
        rv = this._resolveError(req);
      }

      return rv;
    });
  }

  private _resolveRequest({
    req,
    token,
  }: ResolveRequestOptions): HttpRequest<unknown> {
    const newReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        ['traceability-id']: uuid(),
        ['x-fe-version']: this.#configuration.version,
      },
    });

    return newReq;
  }

  private _resolveError(req: HttpRequest<unknown>): Observable<never> {
    this._redirectToLogin();
    const rv = this._throwError(req);
    return rv;
  }

  private _throwError(req: HttpRequest<unknown>): Observable<never> {
    const rv = throwError(
      () =>
        new HttpErrorResponse({
          error: {
            errorCode:
              'ERR.AUTH.ACCESSTOKEN.EXPIRED' /* ApiErrorCodes.ErrAuthAccessTokenExpired */,
          },
          headers: req.headers,
          status: 401,
          statusText: 'Unauthorized',
          url: req.url,
        })
    );
    return rv;
  }

  private _redirectToLogin(): void {
    const action = new Navigate(['/login']);
    this.#store.dispatch(action);
  }

  private _resolveToken(): OperatorFunction<boolean, string> {
    return switchMap((isAuthenticated: boolean) => {
      let rv;
      if (isAuthenticated) {
        rv = this.#authenticationViewModel.accessToken$;
      } else {
        rv = of('');
      }

      return rv;
    });
  }
}
